import moment from "moment";
import { Add } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { ButtonGroup, Card, Grid } from "@mui/material";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    DataTable,
    DataTableStatusCell,
    DatePicker,
    FormField,
    Typography,
    IPropListContainer,
    useCommonComponentContext,
} from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import NotificationService, { IRecordNotification } from "../../services/message/Notification.service";

import { ICodename } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import { IsTrue, MessageType, Mode, ReadStatus, SendStatus } from "../../constants/enums";

export interface IRequestGetpaged {
    byMyself?: number;
    readStatus?: number;
    searchText?: string;
    messageType?: number;
    endTime?: number | string;
    startTime?: number | string;

    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
}

interface IModel {
    requestData: IRequestGetpaged;
    requestDataTemp: IRequestGetpaged;
}

export const getValueNumber1000 = (value: number | string | null | undefined) => {
    if (Helpers.isNullOrEmpty(value) || value === 0 || value === "0") {
        return null;
    } else {
        return !Number.isNaN(Number(value)) ? Number(value) * 1000 : null;
    }
};

export const RenderReadStatusNotifi = (props: { status: number; isDetail?: boolean }) => {
    let data: any;
    const Strings = useStrings();

    switch (props.status) {
        case ReadStatus.Read:
            data = { icon: "done", color: "secondary", title: Strings.NOTIFICATION.READ };
            break;
        case ReadStatus.UnRead:
            data = { icon: "circle", color: "info", title: Strings.NOTIFICATION.UNREAD };
            break;
        default:
            data = { icon: "", color: "secondary", title: "" };
            break;
    }

    return <DataTableStatusCell data={data} isDetail={props.isDetail} />;
};

export const RenderSendStatusNotifi = (props: { status: number; isDetail?: boolean }) => {
    let data: any;
    const Strings = useStrings();

    switch (props.status) {
        case SendStatus.Pending:
            data = { icon: "pending", color: "warning", title: Strings.NOTIFICATION.PENDING };
            break;
        case SendStatus.Sending:
            data = { icon: "timeline", color: "info", title: Strings.NOTIFICATION.SENDING };
            break;
        case SendStatus.Success:
            data = { icon: "done", color: "success", title: Strings.NOTIFICATION.SUCCESS };
            break;
        case SendStatus.Failure:
            data = { icon: "close", color: "error", title: Strings.NOTIFICATION.FAILURE };
            break;
        default:
            data = { icon: "", color: "secondary", title: "" };
            break;
    }

    return <DataTableStatusCell data={data} isDetail={props.isDetail} />;
};

const NotificationListContainer = (props: { isHidenBtnCreate?: boolean; } & IPropListContainer<IRequestGetpaged>) => {
    const Strings = useStrings();

    const { language, clientId, userInfo, serviceCode, onError, onShowLoading, onHideLoading } = useCommonComponentContext();

    const [model, setModel] = useState<IModel>({} as IModel);
    const [dataRows, setDataRows] = useState<IRecordNotification[]>([]);

    const readStatusList = [
        {
            code: ReadStatus.Read,
            name: Strings.NOTIFICATION.READ,
        },
        {
            code: ReadStatus.UnRead,
            name: Strings.NOTIFICATION.UNREAD,
        },
    ];

    const messageTypeList = [
        {
            code: MessageType.PushNotification,
            name: Strings.Common.NOTIFICATION,
        },
    ];

    const byMyselfList: ICodename[] = [
        {
            code: IsTrue.True,
            name: Strings.NOTIFICATION.SENT,
        },
        {
            code: IsTrue.False,
            name: Strings.NOTIFICATION.RECEIVED,
        },
    ];

    const dataColumns = useMemo(() => {
        const newColumns = [
            {
                Header: Strings.NOTIFICATION.TITLE,
                accessor: "title",
                width: "200px",
                Cell: (row: any) => <>{Helpers.renderValueByLanguage({ data: row?.value, currentLanguage: language })}</>,
            },
            {
                Header: Strings.NOTIFICATION.CONTENT,
                accessor: "type",
                width: "150px",
                Cell: ({ value }: any) => <>{messageTypeList.find((el) => `${el.code}` === `${value}`)?.name || ""}</>,
            },
            {
                Header: model.requestData?.byMyself === IsTrue.True ? Strings.NOTIFICATION.SEND_TIME : Strings.NOTIFICATION.RECEIVED_TIME,
                accessor: model.requestData?.byMyself === IsTrue.True ? "sendTime" : "updateTime",
                width: "120px",
                Cell: ({ value }: any) => <>{Helpers.formatDate({ value, format: Constants.DateFormat.DDMMYYYY_HHMMSS })}</>,
            },
            {
                Header: Strings.NOTIFICATION.STATUS,
                accessor: model.requestData?.byMyself === IsTrue.True ? "sendStatus" : "readStatus",
                width: "150px",
                Cell: ({ value }: any) =>
                    model.requestData?.byMyself === IsTrue.True ? (
                        <RenderSendStatusNotifi status={Number(value)} />
                    ) : (
                        <RenderReadStatusNotifi status={Number(value)} />
                    ),
            },
        ];

        return newColumns;
    }, [Strings, messageTypeList, model?.requestData?.byMyself]);

    useEffect(() => {
        let data: IRequestGetpaged = {
            endTime: props.requestGetPaged?.endTime,
            byMyself: props.requestGetPaged?.byMyself,
            startTime: props.requestGetPaged?.startTime,
            searchText: props.requestGetPaged?.searchText,
            readStatus: props.requestGetPaged?.readStatus,
            messageType: props.requestGetPaged?.messageType,
            totalCount: props.requestGetPaged?.totalCount,
            pageNumber: props.requestGetPaged?.pageNumber,
            pageSize: props.requestGetPaged?.pageSize,
        };

        if (!Helpers.isNullOrEmpty(userInfo?.userProfile?.organizationId)) {
            getPaged(data);
        }
    }, [props.requestGetPaged, userInfo?.userProfile?.organizationId]);

    const getPaged = async (dataReq?: IRequestGetpaged) => {
        try {
            onShowLoading();

            const pageSize = dataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(dataReq?.pageNumber || 1, pageSize, dataReq?.totalCount || 0);

            let requestData: any = {
                pageSize,
                pageNumber,
                clientId: clientId,
                serviceCode: serviceCode,
                organizationId: userInfo?.userProfile?.organizationId,

                searchText: dataReq?.searchText,
                readStatus: dataReq?.readStatus,
                messageType: dataReq?.messageType,
                byMyself: Helpers.isNullOrEmpty(dataReq?.byMyself) ? IsTrue.False : dataReq?.byMyself,
                startTime:
                    getValueNumber1000(dataReq?.startTime) !== null
                        ? moment(getValueNumber1000(dataReq?.startTime)).startOf("day").unix()
                        : undefined,
                endTime: getValueNumber1000(dataReq?.endTime) !== null ? moment(getValueNumber1000(dataReq?.endTime)).endOf("day").unix() : undefined,
            };

            const result = await NotificationService.getPaged({
                ...requestData,
                byMyself: requestData?.byMyself === IsTrue.True,
            });

            setDataRows([...(result?.pageValue?.items || [])]);

            setModel({
                requestData: {
                    ...requestData,
                    totalCount: result?.pageValue?.totalCount || 0,
                },
                requestDataTemp: {
                    ...requestData,
                    totalCount: result?.pageValue?.totalCount || 0,
                },
            });

            let query: string = `?pageNumber=${requestData?.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.byMyself)) {
                query += `&byMyself=${requestData?.byMyself}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.readStatus)) {
                query += `&readStatus=${requestData?.readStatus}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.messageType)) {
                query += `&messageType=${requestData?.messageType}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.startTime)) {
                query += `&startTime=${requestData?.startTime}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.endTime)) {
                query += `&endTime=${requestData?.endTime}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.searchText)) {
                query += `&searchText=${requestData?.searchText}`;
            }

            props.onGetPaged({ query: query, totalCount: result?.pageValue?.totalCount || 0 });

            onHideLoading();
        } catch (error: any) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const onValueChange = (newValue: any, key: keyof IRequestGetpaged) => {
        let requestDataTemp: any = { ...model.requestDataTemp };

        let { startTime, endTime } = model?.requestDataTemp;

        switch (key) {
            case "startTime":
                let valStartTime = !Helpers.isNullOrEmpty(newValue) ? moment(newValue).unix() : undefined;
                if (!Helpers.isNullOrEmpty(endTime) && valStartTime > Number(endTime) && !Helpers.isNullOrEmpty(valStartTime)) {
                    endTime = valStartTime;
                }
                requestDataTemp.startTime = valStartTime;
                requestDataTemp.endTime = endTime;
                break;
            case "endTime":
                let valEndTime = !Helpers.isNullOrEmpty(newValue) ? moment(newValue).unix() : undefined;
                if (!Helpers.isNullOrEmpty(startTime) && valEndTime < Number(startTime) && !Helpers.isNullOrEmpty(valEndTime)) {
                    startTime = valEndTime;
                }
                requestDataTemp.startTime = startTime;
                requestDataTemp.endTime = valEndTime;
                break;
            default:
                requestDataTemp[key] = newValue;
                break;
        }
        setModel({ ...model, requestDataTemp });
    };

    const filterForm = () => {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormField
                            maxLength={255}
                            label={Strings.Common.SEARCH_TEXT}
                            placeholder={Strings.Common.ENTER_SEARCH_TEXT}
                            value={model.requestDataTemp?.searchText || ""}
                            onChangeValue={(value) => onValueChange(value, "searchText")}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            data={messageTypeList || []}
                            label={Strings.NOTIFICATION.NOTIFICATION_TYPE}
                            defaultValue={model.requestDataTemp?.messageType}
                            placeholder={Strings.NOTIFICATION.SELECT_NOTIFICATION_TYPE}
                            onChange={(value) => onValueChange(value, "messageType")}
                        />
                    </Grid>

                    {model.requestData?.byMyself === IsTrue.False && (
                        <Grid item xs={12}>
                            <Autocomplete
                                data={readStatusList || []}
                                label={Strings.NOTIFICATION.STATUS}
                                placeholder={Strings.NOTIFICATION.SELECT_STATUS}
                                defaultValue={model.requestDataTemp?.readStatus}
                                onChange={(value) => onValueChange(value, "readStatus")}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography color="secondary" variant="caption" fontWeight="regular" sx={{ lineHeight: 0, fontSize: "0.825rem", mb: 1 }}>
                            {model.requestData?.byMyself === IsTrue.True ? Strings.NOTIFICATION.SEND_DATE : Strings.NOTIFICATION.RECEIVED_DATE}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box sx={{ width: "49%" }}>
                                <DatePicker
                                    label={""}
                                    placeholder={Strings.NOTIFICATION.ENTER_TIME}
                                    value={getValueNumber1000(model.requestDataTemp?.startTime)}
                                    onChangeValue={(e: any) => onValueChange(e, "startTime")}
                                />
                            </Box>
                            <Box sx={{ width: "49%" }}>
                                <DatePicker
                                    label={""}
                                    placeholder={Strings.NOTIFICATION.ENTER_TIME}
                                    value={getValueNumber1000(model.requestDataTemp?.endTime)}
                                    onChangeValue={(e: any) => onValueChange(e, "endTime")}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderChipBox = () =>
        !Helpers.isNullOrEmpty(model.requestData?.startTime) ||
            !Helpers.isNullOrEmpty(model.requestData?.endTime) ||
            !Helpers.isNullOrEmpty(model.requestData?.readStatus) ||
            !Helpers.isNullOrEmpty(model.requestData?.messageType) ? (
            <Box mb={1}>
                {!Helpers.isNullOrEmpty(model.requestData?.readStatus) && (
                    <Chip
                        label={Strings.NOTIFICATION.STATUS}
                        value={readStatusList.find((el) => `${el.code}` === `${model.requestData?.readStatus}`)?.name || ""}
                        onDelete={() => {
                            getPaged({ ...model.requestData, readStatus: undefined });
                        }}
                    />
                )}
                {!Helpers.isNullOrEmpty(model.requestData?.messageType) && (
                    <Chip
                        label={Strings.NOTIFICATION.NOTIFICATION_TYPE}
                        value={messageTypeList.find((el) => `${el.code}` === `${model.requestData?.messageType}`)?.name || ""}
                        onDelete={() => {
                            getPaged({ ...model.requestData, messageType: undefined });
                        }}
                    />
                )}
                {!Helpers.isNullOrEmpty(model.requestData?.startTime) && (
                    <Chip
                        label={Strings.NOTIFICATION.FROM}
                        value={Helpers.formatDate({ value: model.requestData?.startTime })}
                        onDelete={() => {
                            getPaged({ ...model.requestData, startTime: undefined });
                        }}
                    />
                )}
                {!Helpers.isNullOrEmpty(model.requestData?.endTime) && (
                    <Chip
                        label={Strings.NOTIFICATION.TO}
                        value={Helpers.formatDate({ value: model.requestData?.endTime })}
                        onDelete={() => {
                            getPaged({ ...model.requestData, endTime: undefined });
                        }}
                    />
                )}
            </Box>
        ) : (
            <></>
        );

    return (
        <>
            <Grid container spacing={3}>
                {!props.isHidenBtnCreate && (
                    <Grid item xs={12}>
                        <Box justifyContent="flex-end" display="flex">
                            <Button
                                onClick={() => {
                                    props.onNavigate({ mode: Mode.Create });
                                }}
                            >
                                <Add />
                                &nbsp;{Strings.Common.ADD_NEW}
                            </Button>
                        </Box>
                    </Grid>
                )}
                <Grid item xs={12}>
                    {renderChipBox()}
                    <Card>
                        <Box p={2}>
                            <DataTable
                                rowPerPage={model.requestData?.pageSize}
                                pageNumber={model.requestData?.pageNumber}
                                totalCount={model.requestData?.totalCount}
                                onChangePageSize={(pageSize) => {
                                    getPaged({ ...model.requestData, pageSize });
                                }}
                                onChangePageNumber={(pageNumber) => {
                                    getPaged({ ...model.requestData, pageNumber });
                                }}
                                //
                                leftElement={
                                    <>
                                        <ButtonGroup variant="contained">
                                            {byMyselfList.map((item) => (
                                                <Button
                                                    key={"btn_" + item.code}
                                                    color={item.code === model.requestData?.byMyself ? "dark" : "secondary"}
                                                    variant={item.code === model.requestData?.byMyself ? "gradient" : "outlined"}
                                                    onClick={() => {
                                                        getPaged({
                                                            ...model.requestData,
                                                            pageNumber: 1,
                                                            readStatus: undefined,
                                                            byMyself: Number(item.code),
                                                        });
                                                    }}
                                                >
                                                    {item.name}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </>
                                }
                                //
                                placeholderSearch={Strings.Common.SEARCH_TEXT}
                                searchText={model.requestData?.searchText || ""}
                                onSearchChange={(val) => onValueChange(val, "searchText")}
                                onSearch={(val) => {
                                    getPaged({
                                        ...model.requestData,
                                        searchText: val,
                                        pageNumber: 1,
                                    });
                                }}
                                //
                                filterForm={filterForm()}
                                onFilter={() => {
                                    getPaged(model.requestDataTemp);
                                }}
                                onCloseFilter={() => {
                                    const temp = { ...model.requestData };
                                    setModel({ ...model, requestDataTemp: temp });
                                }}
                                onReset={() => {
                                    getPaged({
                                        pageNumber: 1,
                                        pageSize: Constants.ROW_PER_PAGE,
                                    });
                                }}
                                table={{
                                    rows: dataRows || [],
                                    columns: dataColumns,
                                }}
                                actionList={[
                                    {
                                        key: "detail",
                                        iconName: "view",
                                        title: (model.requestData?.byMyself === IsTrue.True)
                                            ? Strings.Common.VIEW_DETAIL
                                            : Strings.NOTIFICATION.SEE_NOTIFICATION,
                                        callback: async (row) => {
                                            props.onNavigate({ id: row?.id, mode: Mode.View });

                                            if ((model.requestData?.byMyself === IsTrue.False)) {
                                                try {
                                                    await NotificationService.readNotificationById(row?.id);
                                                } catch (error) {
                                                    const e = Helpers.renderExceptionError(error);
                                                    console.log("error: ", e);
                                                }
                                            }
                                        },
                                    },
                                ]}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default NotificationListContainer;
